import { apiClient } from './api';

export interface TimeSlot {
  time: string;
}

export interface Booking {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  status: string;
  notes?: string;
  totalPrice: number;
  staffId: string;
  serviceId: string;
  salonId: string;
  clientId: string;
  service: { name: string; duration: number; price: number };
  staff: { id?: string; firstName: string; lastName: string };
  salon: { id?: string; name: string; address?: string };
  client?: { firstName: string; lastName: string; phone?: string };
  payment?: { status: string; method?: PaymentMethod | null } | null;
}

export type PaymentMethod = 'CARD' | 'BLIK' | 'TRANSFER' | 'CASH' | 'DEPOSIT' | 'BARTER' | 'VOUCHER';

interface PaginatedResponse<T> {
  data: T[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

interface CreateBookingData {
  salonId: string;
  serviceId: string;
  /** Wariant uslugi, gdy usluga je ma — stad cena i czas wizyty. */
  serviceVariantId?: string;
  staffId: string;
  date: string;
  startTime: string;
  notes?: string;
  clientId?: string;
}

interface CreatePublicBookingData extends CreateBookingData {
  guest: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
}

export const bookingApi = {
  async getAvailableSlots(params: {
    salonId: string;
    staffId: string;
    serviceId: string;
    date: string;
  }) {
    const res = await apiClient.get<{ slots: string[] }>('/bookings/available-slots', { params });
    return res.data.slots;
  },

  // `force` = zapisz mimo ostrzezen (kolizja, grafik, godziny otwarcia).
  // Backend honoruje te flage wylacznie dla personelu salonu.
  async create(data: CreateBookingData & { force?: boolean }) {
    const res = await apiClient.post<Booking>('/bookings', data);
    return res.data;
  },

  async createPublic(data: CreatePublicBookingData) {
    const res = await apiClient.post<Booking>('/bookings/public', data);
    return res.data;
  },

  async getMyBookings(upcoming = true) {
    const res = await apiClient.get<Booking[]>('/bookings/my', {
      params: { upcoming: upcoming ? 'true' : 'false' },
    });
    return res.data;
  },

  async list(params?: {
    salonId?: string;
    staffId?: string;
    date?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) {
    const res = await apiClient.get<PaginatedResponse<Booking>>('/bookings', { params });
    return res.data;
  },

  async updateStatus(id: string, status: string) {
    const res = await apiClient.patch<Booking>(`/bookings/${id}/status`, { status });
    return res.data;
  },

  // Set the payment method (marks the booking paid). Pass null to clear it.
  // Optional amount overrides the charged sum (finalization with custom price).
  // Edycja wizyty: termin, pracownik lub notatka. Uzywane przez przeciaganie
  // kafla w kalendarzu i przez okno „Edytuj". `force` = zapisz mimo ostrzezen.
  async update(
    id: string,
    data: { date?: string; startTime?: string; staffId?: string; notes?: string; force?: boolean },
  ) {
    const res = await apiClient.patch<Booking>(`/bookings/${id}`, data);
    return res.data;
  },

  async setPayment(id: string, method: PaymentMethod | null, amount?: number) {
    const res = await apiClient.patch<Booking>(`/bookings/${id}/payment`, { method, ...(amount != null ? { amount } : {}) });
    return res.data;
  },

  // Finalizacja: status, platnosc i dosprzedaz w jednym zadaniu. `amount` to
  // kwota za sama wizyte, `extras` to pozycje dolozone przy kasie — kazda
  // wskazuje usluge ALBO produkt.
  async finalize(
    id: string,
    data: {
      paymentMethod: PaymentMethod;
      amount?: number;
      extras?: { serviceId?: string; productId?: string; name: string; quantity: number; unitPrice: number }[];
    },
  ) {
    const res = await apiClient.post<{ booking: Booking; sale: unknown; total: number }>(
      `/bookings/${id}/finalize`,
      data,
    );
    return res.data;
  },
};
